/**
 * SanPlumb — Viewer3D (Fase 3d)
 *
 * Visor isométrico cableado al ENGINE REAL de Fase 2:
 *   - recibe el `SystemData` ya generado (por estrategia) desde ViewerScreen
 *   - `buildSceneFromData(data, scene, layers)` puebla la escena Three.js
 *
 * La cámara, órbita y render loop los maneja `useThreeScene`. Cuando cambian
 * system/capas/animKey se repuebla la escena; cuando cambia la vista se aplica
 * el preset de cámara.
 */

import { useEffect, useRef } from 'react';
import type { CameraView, LayerState, SelectedElement } from '../../types/ui';
import type { SystemData } from '../../types';
import { buildSceneFromData } from '../../engine/three/sceneBuilder';
import type { LayerToggles, ClickableUserData } from '../../engine/three/sceneBuilder';
import { useThreeScene } from '../../hooks/useThreeScene';
import type { SceneControls } from '../../hooks/useThreeScene';

export interface Viewer3DProps {
  system: SystemData;
  layers: LayerState;
  view: CameraView;
  /** Cambia cuando se pulsa "Regenerar 3D" (fuerza repoblar + replay). */
  animKey: number;
  /** Si false, pausa la auto-rotación de la cámara. */
  autoRotate: boolean;
  onSelect: (el: SelectedElement) => void;
}

function toEngineLayers(l: LayerState): LayerToggles {
  return {
    walls: l.muros,
    nptPlanes: l.planos,
    sanitarios: l.sanitarios,
    medidas: l.labelsMedidas,
    showAF: l.showAF,
    showAC: l.showAC,
  };
}

export function Viewer3D({
  system,
  layers,
  view,
  animKey,
  autoRotate,
  onSelect,
}: Viewer3DProps): JSX.Element {
  const { mountRef, controlsRef } = useThreeScene();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Repoblar la escena cuando cambian system / capas / animKey
  useEffect(() => {
    const controls: SceneControls | null = controlsRef.current;
    if (!controls) return;
    const result = buildSceneFromData(system, controls.scene, toEngineLayers(layers));
    controls.setTarget(result.cameraTarget.x, result.cameraTarget.y, result.cameraTarget.z);
    // Registrar los meshes para la animación de entrada (resetea t0 internamente).
    controls.setAnimatables(result.animatables.pipes, result.animatables.fittings);
    controls.replay();
  }, [system, layers, animKey, controlsRef]);

  // Preset de vista
  useEffect(() => {
    controlsRef.current?.setView(view);
  }, [view, controlsRef]);

  // Auto-rotación (pause/play)
  useEffect(() => {
    controlsRef.current?.setAutoRotate(autoRotate);
  }, [autoRotate, controlsRef]);

  // Raycaster para selección por click
  useEffect(() => {
    const el = mountRef.current;
    const controls = controlsRef.current;
    if (!el || !controls) return;

    function onClick(e: MouseEvent): void {
      const c = controlsRef.current;
      const node = mountRef.current;
      if (!c || !node) return;
      const rect = node.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      void import('three').then((THREE) => {
        const ray = new THREE.Raycaster();
        ray.setFromCamera(new THREE.Vector2(x, y), c.camera);
        const hits = ray.intersectObjects(c.scene.children, true);
        const hit = hits.find((hh) => hh.object.userData && 'kind' in hh.object.userData);
        if (!hit) {
          onSelectRef.current({ kind: 'none' });
          return;
        }
        onSelectRef.current(translateSelection(hit.object.userData as ClickableUserData));
      });
    }

    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [mountRef, controlsRef]);

  return <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />;
}

function translateSelection(ud: ClickableUserData): SelectedElement {
  if (ud.kind === 'pipe') {
    const p = ud.data as { system: 'AF' | 'AC'; diam: string; len?: number };
    return {
      kind: 'pipe',
      sys: p.system === 'AC' ? 'ac' : 'af',
      gauge: p.diam === '1/2"' ? 'thin' : 'thick',
      len: p.len ?? 0,
    };
  }
  if (ud.kind === 'sanitary') {
    const s = ud.data as { type: string };
    return { kind: 'apar', label: s.type };
  }
  const a = ud.data as { system: 'AF' | 'AC'; type: string; color?: string };
  const kind = a.color?.includes('reduction')
    ? 'reduction'
    : a.color?.includes('valve') || a.color?.includes('llave')
      ? 'valve'
      : 'fitting';
  return { kind, sys: a.system === 'AC' ? 'ac' : 'af', label: a.type };
}
